import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MerchantHeader from '../components/MerchantHeader';
import ErrorBoundary from '../components/ErrorBoundary';
import { LoadingPage } from '../components/LoadingState';
import { toast } from 'sonner';
import {
  CreditCard, Check, X, Zap, Crown, Building2,
  ArrowRight, Receipt, Download, ChevronRight,
  Shield, Coins, CircleDollarSign, ExternalLink,
  Plus, Minus, ShoppingCart, Bot
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter,
} from '@/components/ui/card';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PLANS = [
  {
    id: 'free', name: 'Free', price: 0, period: 'forever',
    credits: '1,000', rateLimit: '2 req/s', keys: 2, overage: false,
    icon: Zap, color: 'zinc',
    features: [
      { text: '1,000 credits/month', included: true },
      { text: '2 API keys', included: true },
      { text: 'Product search & lookup', included: true },
      { text: 'Basic pricing data', included: true },
      { text: 'Agent commerce', included: false },
      { text: 'Compute+ operations', included: false },
      { text: 'Priority support', included: false },
    ],
  },
  {
    id: 'pro', name: 'Pro', price: 49, period: '/mo',
    credits: '10,000', rateLimit: '10 req/s', keys: 10, overage: true,
    icon: Crown, color: 'cyan', recommended: true,
    features: [
      { text: '10,000 credits/month', included: true },
      { text: '10 API keys', included: true },
      { text: 'Full product intelligence', included: true },
      { text: 'Price optimization & elasticity', included: true },
      { text: 'Agent commerce (negotiate, checkout)', included: true },
      { text: 'Compute+ operations', included: true },
      { text: 'Email support', included: true },
    ],
  },
  {
    id: 'scale', name: 'Scale', price: 199, period: '/mo',
    credits: '100,000', rateLimit: '50 req/s', keys: 50, overage: true,
    icon: Building2, color: 'purple',
    features: [
      { text: '100,000 credits/month', included: true },
      { text: '50 API keys', included: true },
      { text: 'Everything in Pro', included: true },
      { text: 'Global market intelligence', included: true },
      { text: 'Demand forecasting', included: true },
      { text: 'Lower overage rates ($0.006/credit)', included: true },
      { text: 'Priority support + Slack', included: true },
    ],
  },
  {
    id: 'enterprise', name: 'Enterprise', price: 499, period: '/mo',
    credits: '1,000,000', rateLimit: '200 req/s', keys: 500, overage: true,
    icon: Shield, color: 'amber',
    features: [
      { text: '1,000,000 credits/month', included: true },
      { text: '500 API keys', included: true },
      { text: 'Everything in Scale', included: true },
      { text: 'VPS isolation (SSH tunnel)', included: true },
      { text: 'Custom credit allocation', included: true },
      { text: 'Lowest overage rates ($0.004/credit)', included: true },
      { text: 'Dedicated support + SLA', included: true },
    ],
  },
];

export default function SubscriptionPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [currentTier, setCurrentTier] = useState('free');
  const [tierComparison, setTierComparison] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [usdcSettings, setUsdcSettings] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [creditAmount, setCreditAmount] = useState(5000);
  const [buyingCredits, setBuyingCredits] = useState(false);

  const getToken = () => sessionStorage.getItem('session_token')
    || localStorage.getItem('ci_session_token');

  // Preselect a tier from ?plan= (landing credit-track CTAs deep-link here)
  useEffect(() => {
    const wanted = new URLSearchParams(window.location.search).get('plan');
    if (wanted && PLANS.some(p => p.id === wanted)) {
      const plan = PLANS.find(p => p.id === wanted);
      if (plan.id !== 'free') {
        setSelectedPlan(plan);
        setShowUpgradeDialog(true);
      }
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchWithAuth = useCallback(async (endpoint, options = {}) => {
    const token = getToken();
    if (!token) { navigate('/login'); return null; }
    const res = await fetch(`${API_URL}${endpoint}`, {
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      ...options,
    });
    if (!res.ok) throw new Error(`Failed: ${endpoint}`);
    return res.json();
  }, [navigate]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadData = useCallback(async () => {
    try {
      const [balance, tiers, inv, usdc] = await Promise.all([
        fetchWithAuth('/api/v1/billing/balance'),
        fetchWithAuth('/api/v1/billing/compare-tiers'),
        fetchWithAuth('/api/v1/billing/invoices?limit=10').catch(() => ({ invoices: [] })),
        fetchWithAuth('/api/v1/billing/usdc/settings').catch(() => null),
      ]);
      setCurrentTier(balance?.tier || 'free');
      setTierComparison(tiers);
      setInvoices(inv?.invoices || []);
      setUsdcSettings(usdc);
    } catch (err) {
      console.error('Subscription load failed:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleUpgrade = async (planId) => {
    setUpgrading(true);
    try {
      const result = await fetchWithAuth('/api/v1/billing/checkout', {
        method: 'POST',
        body: JSON.stringify({ tier: planId }),
      });
      if (result?.checkout_url) {
        window.location.href = result.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (err) {
      toast.error('Upgrade failed. Please try again.');
    } finally {
      setUpgrading(false);
      setShowUpgradeDialog(false);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await fetchWithAuth('/api/v1/billing/cancel', { method: 'POST' });
      toast.success('Subscription will cancel at end of billing period.');
      loadData();
    } catch (err) {
      toast.error('Cancel failed. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  const handleToggleUsdc = async (enabled) => {
    try {
      const result = await fetchWithAuth('/api/v1/billing/usdc/settings', {
        method: 'PUT',
        body: JSON.stringify({ enabled, settlement_currency: 'usd' }),
      });
      setUsdcSettings(result);
      toast.success(enabled ? 'USDC payments enabled' : 'USDC payments disabled');
    } catch (err) {
      toast.error('Failed to update USDC settings');
    }
  };

  const handleBuyCredits = async () => {
    if (creditAmount < 1000) { toast.error('Minimum purchase is 1,000 credits'); return; }
    setBuyingCredits(true);
    try {
      const result = await fetchWithAuth('/api/v1/billing/checkout', {
        method: 'POST',
        body: JSON.stringify({ credits: creditAmount }),
      });
      if (result?.checkout_url) {
        window.location.href = result.checkout_url;
      } else {
        toast.success(`${creditAmount.toLocaleString()} credits added to your account`);
        loadData();
      }
    } catch (err) {
      toast.error('Credit purchase failed. Please try again.');
    } finally {
      setBuyingCredits(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-zinc-950">
      <MerchantHeader />
      <LoadingPage message="Loading subscription..." />
    </div>
  );

  const borderColors = { zinc: 'border-zinc-700', cyan: 'border-cyan-600', purple: 'border-purple-600', amber: 'border-amber-600' };
  const bgColors = { zinc: '', cyan: 'ring-2 ring-cyan-600/30', purple: 'ring-2 ring-purple-600/20', amber: 'ring-2 ring-amber-600/20' };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <MerchantHeader />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold">Subscription & Billing</h1>
            <p className="text-zinc-400 text-sm mt-1">Manage your plan, payment methods, and invoices</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/dashboard/settings/billing')}>
            <ChevronRight className="w-4 h-4 mr-1 rotate-180" /> Usage Dashboard
          </Button>
        </div>

        <Tabs defaultValue="plans">
          <TabsList className="bg-zinc-900 border-zinc-800">
            <TabsTrigger value="plans">Plans</TabsTrigger>
            <TabsTrigger value="invoices">Invoices</TabsTrigger>
            <TabsTrigger value="payment">Payment Methods</TabsTrigger>
          </TabsList>

          {/* Plans Tab */}
          <TabsContent value="plans">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {PLANS.map((plan) => {
                const Icon = plan.icon;
                const isCurrent = plan.id === currentTier;
                const comparison = tierComparison?.comparisons?.find(c => c.tier === plan.id);

                return (
                  <Card key={plan.id} className={`bg-zinc-900/70 ${borderColors[plan.color]} ${isCurrent ? bgColors[plan.color] : ''} relative`}>
                    {plan.recommended && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <Badge className="bg-cyan-600 text-white text-xs">Recommended</Badge>
                      </div>
                    )}
                    {isCurrent && (
                      <div className="absolute -top-3 right-4">
                        <Badge className="bg-emerald-600 text-white text-xs">Current Plan</Badge>
                      </div>
                    )}
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon className={`w-5 h-5 text-${plan.color}-400`} />
                        <CardTitle className="text-lg">{plan.name}</CardTitle>
                      </div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold">${plan.price}</span>
                        <span className="text-zinc-500 text-sm">{plan.period}</span>
                      </div>
                      <CardDescription className="text-xs mt-1">
                        {plan.credits} credits/mo &middot; {plan.rateLimit}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pb-3">
                      <ul className="space-y-2">
                        {plan.features.map((f, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs">
                            {f.included
                              ? <Check className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />
                              : <X className="w-3.5 h-3.5 text-zinc-600 mt-0.5 flex-shrink-0" />
                            }
                            <span className={f.included ? 'text-zinc-300' : 'text-zinc-600'}>{f.text}</span>
                          </li>
                        ))}
                      </ul>
                      {comparison && !isCurrent && (
                        <div className="mt-4 p-2 rounded bg-zinc-800/50 text-xs text-zinc-400">
                          Based on your usage: <span className="text-cyan-400">${comparison.total_cost_usd}/mo</span>
                        </div>
                      )}
                    </CardContent>
                    <CardFooter>
                      {isCurrent ? (
                        plan.id !== 'free' ? (
                          <Button variant="outline" size="sm" className="w-full text-xs" onClick={handleCancel} disabled={cancelling}>
                            {cancelling ? 'Cancelling...' : 'Cancel Plan'}
                          </Button>
                        ) : (
                          <Button variant="outline" size="sm" className="w-full text-xs" disabled>Current Plan</Button>
                        )
                      ) : (
                        <Button
                          size="sm"
                          className={`w-full text-xs ${plan.recommended ? 'bg-cyan-600 hover:bg-cyan-700' : ''}`}
                          onClick={() => { setSelectedPlan(plan); setShowUpgradeDialog(true); }}
                          disabled={upgrading}
                        >
                          {plan.price > (PLANS.find(p => p.id === currentTier)?.price || 0) ? 'Upgrade' : 'Switch'} to {plan.name}
                        </Button>
                      )}
                    </CardFooter>
                  </Card>
                );
              })}
            </div>
            {/* Buy Credits Card */}
            <Card className="bg-zinc-900/70 border-zinc-800 mt-6">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Coins className="w-4 h-4 text-amber-400" /> Buy Additional Credits
                </CardTitle>
                <CardDescription>
                  Top up your credit balance. Credits never expire and roll over each month.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setCreditAmount(Math.max(1000, creditAmount - 1000))}>
                      <Minus className="w-3.5 h-3.5" />
                    </Button>
                    <div className="w-32 text-center">
                      <input
                        type="number"
                        value={creditAmount}
                        onChange={(e) => setCreditAmount(Math.max(1000, parseInt(e.target.value) || 1000))}
                        step={1000}
                        min={1000}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-md px-3 py-1.5 text-sm text-center text-zinc-200 focus:outline-none focus:ring-1 focus:ring-cyan-600"
                      />
                    </div>
                    <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setCreditAmount(creditAmount + 1000)}>
                      <Plus className="w-3.5 h-3.5" />
                    </Button>
                    <span className="text-xs text-zinc-500">credits</span>
                  </div>
                  <div className="flex-1 text-right">
                    <p className="text-lg font-bold">${(creditAmount * 0.008).toFixed(2)}</p>
                    <p className="text-[10px] text-zinc-500">$0.008 per credit</p>
                  </div>
                  <Button className="bg-amber-600 hover:bg-amber-700" onClick={handleBuyCredits} disabled={buyingCredits}>
                    <ShoppingCart className="w-4 h-4 mr-1" />
                    {buyingCredits ? 'Processing...' : 'Buy Credits'}
                  </Button>
                </div>
                <div className="mt-3 grid grid-cols-4 gap-2">
                  {[1000, 5000, 10000, 50000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setCreditAmount(amount)}
                      className={`py-1.5 px-2 rounded text-xs border transition-colors ${
                        creditAmount === amount
                          ? 'border-amber-600 bg-amber-900/20 text-amber-400'
                          : 'border-zinc-800 bg-zinc-800/30 text-zinc-400 hover:border-zinc-700'
                      }`}
                    >
                      {amount.toLocaleString()} credits &middot; ${(amount * 0.008).toFixed(2)}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices">
            <Card className="bg-zinc-900/70 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-base">Invoice History</CardTitle>
                <CardDescription>Recent payments and invoices</CardDescription>
              </CardHeader>
              <CardContent>
                {invoices.length === 0 ? (
                  <div className="text-center py-12">
                    <Receipt className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                    <p className="text-zinc-500 text-sm">No invoices yet</p>
                    <p className="text-zinc-600 text-xs mt-1">Invoices will appear here after your first payment</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {invoices.map((inv, i) => (
                      <div key={i} className="flex items-center justify-between py-3 border-b border-zinc-800/50 last:border-0">
                        <div className="flex items-center gap-3">
                          <Receipt className="w-4 h-4 text-zinc-500" />
                          <div>
                            <p className="text-sm">{inv.stripe_invoice_id || `Invoice #${i + 1}`}</p>
                            <p className="text-xs text-zinc-500">{inv.created_at?.slice(0, 10)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium">${inv.amount_paid_usd?.toFixed(2)}</span>
                          <Badge variant="outline" className={`text-xs ${inv.status === 'paid' ? 'text-emerald-400 border-emerald-800' : 'text-amber-400 border-amber-800'}`}>
                            {inv.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payment Methods Tab */}
          <TabsContent value="payment">
            <div className="grid gap-6 md:grid-cols-2">
              <Card className="bg-zinc-900/70 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <CreditCard className="w-4 h-4" /> Card Payments
                  </CardTitle>
                  <CardDescription>Managed via Stripe — 2.9% + $0.30 per transaction</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-zinc-400 mb-4">Card payment details are managed securely through Stripe. Click below to update.</p>
                  <Button variant="outline" size="sm">
                    <ExternalLink className="w-3.5 h-3.5 mr-1" /> Manage in Stripe
                  </Button>
                </CardContent>
              </Card>

              <Card className="bg-zinc-900/70 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Coins className="w-4 h-4 text-cyan-400" /> USDC Payments
                  </CardTitle>
                  <CardDescription>
                    Stablecoin payments — <span className="text-cyan-400">1.5% fee</span> (53% cheaper than cards)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-sm font-medium">Accept USDC</p>
                      <p className="text-xs text-zinc-500">Pay with USDC stablecoin via Stripe</p>
                    </div>
                    <Switch
                      checked={usdcSettings?.merchant_enabled || false}
                      onCheckedChange={handleToggleUsdc}
                    />
                  </div>
                  {usdcSettings?.merchant_enabled && (
                    <div className="p-3 rounded-lg bg-zinc-800/50 text-xs space-y-2">
                      <div className="flex justify-between">
                        <span className="text-zinc-400">Settlement</span>
                        <span className="text-zinc-300">{usdcSettings.settlement_currency?.toUpperCase()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-400">Fee</span>
                        <span className="text-cyan-400">1.5% (vs 2.9% card)</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-400">Savings</span>
                        <span className="text-emerald-400">${usdcSettings.savings_vs_cards_usd?.toFixed(2) || '0.00'} saved</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Upgrade Confirmation Dialog */}
        <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle>Upgrade to {selectedPlan?.name}</DialogTitle>
              <DialogDescription>
                {selectedPlan && (
                  <>
                    You'll be charged ${selectedPlan.price}/month for {selectedPlan.credits} credits/month
                    and {selectedPlan.rateLimit} rate limit. You can cancel anytime.
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUpgradeDialog(false)}>Cancel</Button>
              <Button className="bg-cyan-600 hover:bg-cyan-700" onClick={() => handleUpgrade(selectedPlan?.id)} disabled={upgrading}>
                {upgrading ? 'Processing...' : `Upgrade to ${selectedPlan?.name}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
